import { isWithinInterval } from 'date-fns';

import { getQuickLookDate, nonNullable } from './helpers/utils';
import {
  Activity,
  ActivityStatus,
  ActivityType,
  Schedule,
  ScheduleActivityEntity,
  ScheduleFilters,
} from './types';
import {
  fetchWithPagination,
  getRequestWithAllPages,
  getBaseScheduleBEUrl,
} from '../helpers';

const SCHEDULE_BASE_URL = getBaseScheduleBEUrl();

const activitiesCache = new Map<string, Activity[]>();
const schedulesCache: Schedule[] = [];

export async function getEntitiesByIds({
  ids: rawEntityIds,
  projectId,
}: {
  ids: string[];
  projectId: string;
}) {
  // Entity ids format has changed since March 2021
  // The new format is compound by `scheduleId-activityId`
  // where scheduleId and activityId are UUIDs
  const entityIds = rawEntityIds.map((entityId: string) => {
    const parts = entityId.split('-').slice(0, 6);
    const activityUniqueId = parts.pop();
    const scheduleId = parts.join('-');
    return { entityId, activityUniqueId, scheduleId };
  });

  const schedules = await getRequestWithAllPages<Schedule>(
    `${SCHEDULE_BASE_URL}/projects/${projectId}/schedules`,
  );

  const scheduleActivityMap = entityIds.reduce(
    (map, curr) => {
      map[curr.scheduleId] = (map[curr.scheduleId] ?? []).concat(curr);
      return map;
    },
    {} as Record<string, typeof entityIds>,
  );

  const results = await Promise.all(
    Object.entries(scheduleActivityMap).map(async ([scheduleId, entries]) => {
      const escapedScheduleId = encodeURIComponent(scheduleId);
      const escapedUniqueIds = entries
        .map(({ activityUniqueId: activityId }) => activityId)
        .filter(nonNullable)
        .map(encodeURIComponent)
        .join(',');

      return await fetchWithPagination<ScheduleActivityEntity>(
        `${SCHEDULE_BASE_URL}/projects/${projectId}/activities:search?includePath=true&scheduleId=${escapedScheduleId}&activityUniqueIds=${escapedUniqueIds}`,
      );
    }),
  );

  const flattenedResults = results.flat();

  /* RPP expects each entity to be accompanied by the id from the function signature,
   * otherwise it errs.
   */
  const data = flattenedResults
    .map((activity) => {
      const entityId = entityIds.find(
        (entity) =>
          entity.activityUniqueId === activity.uniqueId.toString() &&
          entity.scheduleId === activity.scheduleId,
      )?.entityId;
      if (entityId) {
        const schedule = schedules.find(
          (schedule: Schedule) => schedule.id === activity.scheduleId,
        );
        return {
          id: entityId,
          activity: {
            ...activity,
            scheduleName: schedule?.name,
            permittedActions: schedule?.permittedActions,
          },
        };
      }
      return undefined;
    })
    .filter(nonNullable);

  return {
    data,
  };
}

export const fetchProjectSchedules = async (
  projectId: string,
  userId?: string | null,
) => {
  const schedules = await getRequestWithAllPages<Schedule>(
    `${SCHEDULE_BASE_URL}/projects/${projectId}/schedules`,
  );
  schedulesCache.push(...schedules);
  const recentlySeenScheduleId =
    userId !== null
      ? localStorage.getItem(
          `acs-schedule-user-${userId}-project-${projectId}-last-viewed`,
        )
      : null;

  schedules.sort((a: Schedule, b: Schedule) => {
    if (a.id === recentlySeenScheduleId) {
      return -1;
    }

    if (b.id === recentlySeenScheduleId) {
      return 1;
    }
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  });
  return schedules.filter((schedule) =>
    schedule.permittedActions.includes('edit'),
  );
};

const addActivitiesToCache = (scheduleId: string, activities: Activity[]) => {
  activitiesCache.set(scheduleId, activities);
};

const getActivitiesFromCache = (scheduleId: string) => {
  return activitiesCache.get(scheduleId) ?? [];
};

const readActivities = async (projectId: string, scheduleId: string) => {
  if (!activitiesCache.has(projectId)) {
    const activities = await getRequestWithAllPages<Activity>(
      `${SCHEDULE_BASE_URL}/projects/${projectId}/schedules/${scheduleId}/activities`,
      {
        limit: 500,
      },
    );
    const newActivities = activities.map((activity) => {
      const wbsPathActivities = activities.filter((wbsActivity) =>
        activity.wbsPath?.includes(parseInt(wbsActivity.uniqueId)),
      );
      const schedule = schedulesCache.find(
        (schedule) => schedule.id === activity.scheduleId,
      );
      const scheduleName = schedule?.name;
      const permittedActions = schedule?.permittedActions;
      const name = activity.name ?? 'Untitled Activity';
      const startDateToDisplay =
        activity.actualStart ?? activity.start ?? activity.plannedStart;
      const endDateToDisplay =
        activity.actualFinish ?? activity.finish ?? activity.plannedFinish;

      return {
        ...activity,
        name,
        scheduleName,
        permittedActions,
        wbsPathActivities,
        startDateToDisplay,
        endDateToDisplay,
      };
    });
    addActivitiesToCache(scheduleId, newActivities);
  }

  return getActivitiesFromCache(scheduleId);
};

export function isActivityMatchingFilters(
  activity: Activity,
  filters: ScheduleFilters,
) {
  if (filters.milestone && activity.type !== ActivityType.Milestone) {
    return false;
  }

  if (filters.critical && !activity.isCriticalPath) {
    return false;
  }

  if (filters?.statuses?.length) {
    const statusMatched = filters.statuses.some((status) => {
      switch (status) {
        default:
        case ActivityStatus.NOT_STARTED:
          return activity.completionPercentage === 0;
        case ActivityStatus.IN_PROGRESS:
          return (
            activity.completionPercentage > 0 &&
            activity.completionPercentage < 1
          );
        case ActivityStatus.COMPLETE:
          return activity.completionPercentage === 1;
      }
    });

    if (!statusMatched) {
      return false;
    }
  }
  if (filters.resources?.length) {
    const resourceMatched = activity.assignees.some((assignee) =>
      filters.resources?.find((resource) => resource === assignee.name),
    );

    if (!resourceMatched) {
      return false;
    }
  }

  const activityStart = new Date(activity.startDateToDisplay);
  const activityFinish = new Date(activity.endDateToDisplay);

  if (filters?.dateRange?.[0] && filters?.dateRange?.[1]) {
    const [filterStartDate, filterEndDate] = filters.dateRange;
    const dateMatched =
      isWithinInterval(activityStart, {
        start: filterStartDate,
        end: filterEndDate,
      }) ||
      isWithinInterval(activityFinish, {
        start: filterStartDate,
        end: filterEndDate,
      }) ||
      isWithinInterval(filterStartDate, {
        start: activityStart,
        end: activityFinish,
      }) ||
      isWithinInterval(filterEndDate, {
        start: activityStart,
        end: activityFinish,
      });

    if (!dateMatched) {
      return false;
    }
  }

  if (filters?.quickLook?.[0] && filters?.quickLook?.[1]) {
    const backFilter = getQuickLookDate(filters.quickLook[0], false);
    const aheadFilter = getQuickLookDate(filters.quickLook[1], true);

    const dateMatched =
      isWithinInterval(activityStart, {
        start: backFilter,
        end: aheadFilter,
      }) ||
      isWithinInterval(activityFinish, {
        start: backFilter,
        end: aheadFilter,
      }) ||
      isWithinInterval(backFilter, {
        start: activityStart,
        end: activityFinish,
      }) ||
      isWithinInterval(aheadFilter, {
        start: activityStart,
        end: activityFinish,
      });

    if (!dateMatched) {
      return false;
    }
  }

  const activityCodeFiltersKeys = Object.keys(filters).filter((key) =>
    key.includes('activityCode'),
  );

  if (activityCodeFiltersKeys?.length) {
    const activityCodes = activity.activityCodes ?? [];

    for (const filterKey of activityCodeFiltersKeys) {
      const filterValue = (filters as any)[filterKey]; // eslint-disable-line @typescript-eslint/no-explicit-any
      const filterName = filterKey.split('activityCode')[1];
      const activityCode = activityCodes.find(
        ({ name }) => filterName === name,
      );
      if (!activityCode || !filterValue.includes(activityCode.value)) {
        return false;
      }
    }
  }

  // Search Expression match
  if (filters.searchExpression && filters.searchExpression.length >= 1) {
    const name = activity.name.toLowerCase();
    const fileActivityId = activity.fileActivityId?.toLowerCase();

    return (
      name.includes(filters.searchExpression.toLowerCase()) ||
      (fileActivityId &&
        fileActivityId.includes(filters.searchExpression.toLowerCase()))
    );
  }
  return true;
}

export function getActivityParentIds(
  activities: Activity[],
  activity: Activity | undefined,
  ids: string[] = [],
): string[] {
  if (!activity) {
    return ids;
  }

  if (!activity.parentId || parseInt(activity.parentId?.toString()) === 0) {
    return ids.concat([activity.uniqueId.toString()]);
  }

  return getActivityParentIds(
    activities,
    activities.find((a) => a.uniqueId === activity.parentId),
    ids.concat([activity.uniqueId.toString()]),
  );
}

const filterActivities = (activities: Activity[], filters: ScheduleFilters) => {
  // Gets all filtered activity ids matching the filter without duplicates

  const filteredActivityIds = Array.from(
    new Set(
      activities.reduce(
        (
          matchingActivities: string[],
          activity: Activity,
          index: number,
          allActivities: Activity[],
        ) =>
          isActivityMatchingFilters(activity, filters)
            ? matchingActivities.concat(
                getActivityParentIds(allActivities, activity),
              )
            : matchingActivities,
        [],
      ),
    ),
  );
  // Gets the activities matching the filters by the ids we got before
  return activities
    .filter((activity) =>
      filteredActivityIds.includes(activity.uniqueId as unknown as string),
    )
    .map((activity) => {
      const id = `${activity.scheduleId}-${activity.uniqueId}`;
      return {
        id,
        activity,
      };
    });
};

export const queryEntities = async ({
  filters,
  projectId,
}: {
  filters: ScheduleFilters;
  projectId: string;
}) => {
  const { scheduleId } = filters;
  if (scheduleId) {
    const activities = await readActivities(projectId, scheduleId);
    return { data: filterActivities(activities, filters) };
  } else {
    return { data: [] };
  }
};
